import { Box, CircularProgress, InputBase, Typography } from '@material-ui/core';
import { useTheme, withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce/lib';

import Autocomplete from 'evee-ui-common/components/base/inputs/AutoComplete';
import { apiService } from 'evee-ui-common/services';
import useRegion from 'store/modules/app/hooks/useRegion';

import { getDefaultLocationOptions, getLocationOptionIcon } from '../utils/locationOptions';

import styles from './styles';

const INPUT_DEBOUNCE_DELAY = 300;

const FullscreenLocationInput = ({ value, placeholder, className, classes, onChange }) => {
  const theme = useTheme();
  const { region } = useRegion();
  const [inputValue, setInputValue] = useState('');
  const [debouncedInputValue] = useDebounce(inputValue, INPUT_DEBOUNCE_DELAY);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const hasSearchText = Boolean(inputValue?.trim());

  const displayOptions = useMemo(() => {
    if (hasSearchText) {
      return [...options];
    }

    return getDefaultLocationOptions();
  }, [hasSearchText, options]);

  const retrieveOptions = async (searchString) => {
    const trimmed = searchString?.trim();

    if (!trimmed) {
      setLoading(false);
      setOptions([]);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    setLoading(true);

    try {
      const result = await apiService.location.getSearchLocations(trimmed);

      if (currentRequestId === requestIdRef.current) {
        setOptions(result ? result.slice(0, 5) : []);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    retrieveOptions(debouncedInputValue);
  }, [debouncedInputValue]);

  const onInputChange = (_event, newInputValue) => {
    setInputValue(newInputValue ?? '');
  };

  const noOptionsText = hasSearchText
    ? 'No matching location found'
    : 'Start typing to choose location';

  return (
    <Autocomplete
      autoComplete
      disablePortal
      includeInputInList
      filterSelectedOptions
      clearOnBlur={false}
      value={value}
      options={displayOptions}
      noOptionsText={noOptionsText}
      getOptionSelected={(option, val) => option?.name === val?.name}
      getOptionLabel={(option) => option?.name || ''}
      filterOptions={(x) => x}
      loading={loading}
      renderOption={(option) => (
        <Box className={classes.optionContainer}>
          {getLocationOptionIcon(option.icon)}
          <Typography style={{ lineHeight: 1.1 }}>{option.name}</Typography>
        </Box>
      )}
      renderInput={(params) => (
        <div ref={params.InputProps.ref} className={className}>
          <InputBase
            fullWidth
            placeholder={placeholder || region.name}
            inputProps={{ ...params.inputProps, style: { padding: 0 } }}
            style={{
              fontSize: '1.25rem',
              paddingBottom: theme.spacing(0.5),
              borderBottom: `1px solid ${theme.palette.secondary.main}`,
            }}
            endAdornment={
              loading ? (
                <CircularProgress color="inherit" size={20} style={{ marginRight: 8 }} />
              ) : null
            }
          />
        </div>
      )}
      classes={{
        paper: classes.paper,
        listbox: classes.listbox,
        option: classes.option,
        popper: classes.popper,
      }}
      open={displayOptions.length > 0}
      onClose={() => {}}
      onInputChange={onInputChange}
      onChange={(_event, newValue) => {
        if (newValue) {
          onChange(newValue);
        }
      }}
    />
  );
};

FullscreenLocationInput.propTypes = {
  value: PropTypes.object,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  classes: PropTypes.shape({
    popper: PropTypes.string,
    paper: PropTypes.string,
    listbox: PropTypes.string,
    optionContainer: PropTypes.string,
    option: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func,
};

FullscreenLocationInput.defaultProps = {
  value: {},
  placeholder: '',
  className: '',
  onChange: () => {},
};

export default withStyles(styles)(FullscreenLocationInput);
